import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FileText, FolderOpen, IndianRupee, Landmark, PanelsTopLeft, ScanLine, Sun, Wrench } from "lucide-react";
import { Reveal } from "./primitives";
import { APP_SHOT_URL } from "../../lib/site";

const FEATURES = [
  {
    icon: Sun,
    tag: "Live Generation",
    body: "See exactly how much your panels produced today.",
    color: "text-amber",
    ring: "group-hover:border-amber/50",
  },
  {
    icon: IndianRupee,
    tag: "Bill Tracking",
    body: "Watch your savings grow. Real numbers.",
    color: "text-teal",
    ring: "group-hover:border-teal/50",
  },
  {
    icon: ScanLine,
    tag: "Smart Solar Assessment",
    body: "Understand your solar opportunity based on your energy requirement and property information.",
    color: "text-amber",
    ring: "group-hover:border-amber/50",
  },
  {
    icon: FolderOpen,
    tag: "Document Centre",
    body: "Keep every important solar document in one place.",
    color: "text-teal",
    ring: "group-hover:border-teal/50",
  },
  {
    icon: PanelsTopLeft,
    tag: "System Recommendation",
    body: "Get a system recommendation based on your energy requirement and site information.",
    color: "text-teal",
    ring: "group-hover:border-teal/50",
  },
  {
    icon: FileText,
    tag: "Transparent Proposal",
    body: "Understand your proposed system, its expected generation and the commercial offer, clearly.",
    color: "text-amber",
    ring: "group-hover:border-amber/50",
  },
  {
    icon: Landmark,
    tag: "Financing Guidance",
    body: "Explore applicable payment and financing routes and follow the process.",
    color: "text-teal",
    ring: "group-hover:border-teal/50",
  },
  {
    icon: Wrench,
    tag: "Service Request",
    body: "Something off? Tap. We're on it.",
    color: "text-sunrise",
    ring: "group-hover:border-sunrise/50",
  },
];

export const Product = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], ["14%", "-14%"]);

  return (
    <section
      ref={ref}
      id="product"
      className="relative bg-navy text-white py-24 sm:py-32 lg:py-40 overflow-hidden grain"
      data-testid="product-section"
    >
      {/* ambient glows */}
      <div className="pointer-events-none absolute top-1/4 -left-20 h-96 w-96 rounded-full bg-teal/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-96 w-96 rounded-full bg-amber/20 blur-[120px]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Reveal>
            <p className="eyebrow text-amber-400 mb-6">The product</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="display-serif text-4xl sm:text-5xl lg:text-[4rem] leading-[1.0] tracking-[-0.02em]">
              India's first Home Energy OS
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-lg sm:text-xl text-white/65">
              Not just an app. The operating system for your home's energy.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 lg:mt-24 grid items-start gap-12 lg:grid-cols-[1fr_auto_1fr] lg:gap-8">
          {/* left features */}
          <div className="flex flex-col gap-6 lg:pt-8">
            {FEATURES.slice(0, 4).map((f, i) => (
              <FeatureCard key={f.tag} f={f} index={i} align="right" />
            ))}
          </div>

          {/* phone */}
          <motion.div style={{ y: phoneY }} className="mx-auto max-w-full lg:mx-0 order-first lg:order-none">
            <div className="floaty relative max-w-full px-0 py-10 sm:px-14">
              <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-b from-amber/25 via-transparent to-teal/25 blur-2xl" />
              <div className="absolute left-0 top-12 z-30 w-[190px] rounded-2xl border border-amber/25 bg-navy-900/75 p-4 text-left shadow-2xl shadow-black/25 backdrop-blur-xl sm:left-2 sm:w-[210px]">
                <p className="eyebrow text-amber-400">SunShell One App</p>
                <p className="mt-2 text-sm font-medium leading-5 text-white/72">
                  One place to assess, decide, finance and track your solar journey.
                </p>
              </div>
              <div className="relative w-[250px] sm:w-[300px] rounded-[2.6rem] border-[10px] border-navy-900 bg-navy-900 shadow-2xl shadow-black/50 ring-1 ring-white/10">
                <div className="absolute left-1/2 top-2.5 z-20 h-5 w-24 -translate-x-1/2 rounded-full bg-navy-900" />
                <div className="overflow-hidden rounded-[1.9rem]">
                  <img
                    src={APP_SHOT_URL}
                    alt="SunShell One app dashboard showing project, savings and installation progress"
                    className="w-full h-auto block"
                    data-testid="app-screenshot"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* right feature */}
          <div className="flex flex-col gap-6 lg:pt-8">
            {FEATURES.slice(4).map((f, i) => (
              <FeatureCard key={f.tag} f={f} index={i + 4} align="left" />
            ))}
            <Reveal delay={0.2}>
              <p className="display-serif text-2xl text-white/40 italic lg:pl-2">
                See everything. Tap anything.
              </p>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-20 text-center display-serif text-2xl sm:text-3xl text-amber-400">
            SunShell One. Own Your Power.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const FeatureCard = ({ f, index, align }) => (
  <Reveal delay={0.08 * index}>
    <div
      className={`group rounded-[1.4rem] border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-[transform,border-color] duration-300 hover:-translate-y-1 ${f.ring} ${
        align === "right" ? "lg:text-right" : ""
      }`}
      data-testid={`feature-${index}`}
    >
      <span
        className={`inline-grid h-12 w-12 place-items-center rounded-xl bg-white/8 ${f.color} ${
          align === "right" ? "lg:ml-auto" : ""
        }`}
      >
        <f.icon size={24} strokeWidth={1.8} />
      </span>
      <h3 className="mt-4 text-xl font-semibold tracking-tight">{f.tag}</h3>
      <p className="mt-1.5 text-white/60 leading-relaxed">{f.body}</p>
    </div>
  </Reveal>
);
